import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ArrowLeft, Plus } from "lucide-react"
import * as React from "react"
import { toast } from "sonner"
import { applicationsApi, type ApplicationListItem } from "@/api/applications"
import { ApiError } from "@/api/client"
import { inboxApi, type ThreadSummary } from "@/api/inbox"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { Skeleton } from "@/components/ui/skeleton"
import { PageHeader } from "@/components/dashboard/page-header"
import { cn, formatDate } from "@/lib/utils"

export function InboxPage() {
  const [selected, setSelected] = React.useState<string | null>(null)
  const [composing, setComposing] = React.useState(false)
  const qc = useQueryClient()
  const threads = useQuery({ queryKey: ["inbox-threads"], queryFn: () => inboxApi.list() })

  React.useEffect(() => {
    if (!selected && threads.data?.data.length) setSelected(threads.data.data[0].id)
  }, [selected, threads.data])

  return (
    <>
      <PageHeader
        title="Gelen kutusu"
        description="Adaylarla yapılan e-posta yazışmaları."
        action={
          <Button onClick={() => setComposing(true)}>
            <Plus className="size-4" /> Yeni e-posta
          </Button>
        }
      />
      {composing && (
        <ComposeThread
          onCancel={() => setComposing(false)}
          onCreated={(threadId) => {
            setComposing(false)
            setSelected(threadId)
            qc.invalidateQueries({ queryKey: ["inbox-threads"] })
          }}
        />
      )}
      <div className="grid h-[calc(100vh-9rem)] grid-cols-1 gap-0 border-t md:grid-cols-[320px_1fr]">
        <div
          className={cn(
            "overflow-y-auto border-r",
            selected && "hidden md:block",
          )}
        >
          {threads.isLoading && (
            <div className="space-y-2 p-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="space-y-2 rounded-md p-2">
                  <Skeleton className="h-3 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              ))}
            </div>
          )}
          {threads.data?.data.length === 0 && (
            <p className="p-4 text-sm text-muted-foreground">
              Henüz yazışma yok. Aday detayından e-posta gönderebilirsiniz.
            </p>
          )}
          <ul className="divide-y">
            {threads.data?.data.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  className={cn(
                    "block w-full p-3 text-left text-sm hover:bg-accent",
                    selected === t.id && "bg-accent",
                  )}
                  onClick={() => setSelected(t.id)}
                >
                  <ThreadRow thread={t} />
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div
          className={cn(
            "overflow-y-auto",
            !selected && "hidden md:block",
          )}
        >
          {selected ? (
            <ThreadView threadId={selected} onBack={() => setSelected(null)} />
          ) : (
            <p className="p-6 text-muted-foreground">Bir thread seçin.</p>
          )}
        </div>
      </div>
    </>
  )
}

function ComposeThread({
  onCancel,
  onCreated,
}: {
  onCancel: () => void
  onCreated: (threadId: string) => void
}) {
  const [applicationId, setApplicationId] = React.useState("")
  const [subject, setSubject] = React.useState("")
  const [body, setBody] = React.useState("")
  const apps = useQuery({ queryKey: ["applications"], queryFn: () => applicationsApi.list() })

  const create = useMutation({
    mutationFn: () =>
      inboxApi.create({ application_id: applicationId, subject, body }),
    onSuccess: (res) => {
      toast.success("E-posta gönderildi.")
      onCreated(res.data.thread_id)
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "Gönderilemedi."),
  })

  return (
    <div className="border-b bg-muted/30 p-4">
      <Card>
        <CardContent className="grid gap-3 p-4 md:grid-cols-2">
          <div className="space-y-1.5 md:col-span-2">
            <Label>Başvuru</Label>
            <Select value={applicationId} onValueChange={setApplicationId}>
              <SelectTrigger>
                <SelectValue placeholder="Aday başvurusu seçin" />
              </SelectTrigger>
              <SelectContent>
                {(apps.data?.data ?? []).map((a: ApplicationListItem) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.candidate.full_name} — {a.job.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Konu</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Mesaj</Label>
            <Textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} />
          </div>
          <div className="md:col-span-2 flex justify-end gap-2">
            <Button variant="outline" onClick={onCancel}>
              Vazgeç
            </Button>
            <Button
              onClick={() => create.mutate()}
              disabled={!applicationId || !subject.trim() || !body.trim() || create.isPending}
            >
              Gönder
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function ThreadRow({ thread }: { thread: ThreadSummary }) {
  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="truncate font-medium">{thread.candidate?.full_name ?? "—"}</span>
        <span className="shrink-0 text-xs text-muted-foreground">{formatDate(thread.last_message_at)}</span>
      </div>
      <div className="truncate text-xs text-muted-foreground">{thread.subject ?? "(konu yok)"}</div>
      {thread.last_preview && (
        <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">{thread.last_preview}</div>
      )}
      {thread.job && (
        <Badge variant="outline" className="mt-1">
          {thread.job.title}
        </Badge>
      )}
    </>
  )
}

function ThreadView({ threadId, onBack }: { threadId: string; onBack?: () => void }) {
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ["inbox-thread", threadId],
    queryFn: () => inboxApi.get(threadId),
  })
  const [reply, setReply] = React.useState("")

  const send = useMutation({
    mutationFn: () => inboxApi.reply(threadId, { body: reply.trim() }),
    onSuccess: () => {
      setReply("")
      qc.invalidateQueries({ queryKey: ["inbox-thread", threadId] })
      qc.invalidateQueries({ queryKey: ["inbox-threads"] })
      toast.success("Yanıt gönderildi.")
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "Gönderilemedi."),
  })

  if (q.isLoading)
    return (
      <div className="space-y-3 p-6">
        <Skeleton className="h-5 w-1/3" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    )
  if (!q.data) return <p className="p-6 text-muted-foreground">Thread bulunamadı.</p>
  const t = q.data.data

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-start gap-3 border-b p-4">
        {onBack && (
          <Button
            variant="ghost"
            size="icon"
            aria-label="Listeye dön"
            onClick={onBack}
            className="md:hidden"
          >
            <ArrowLeft className="size-4" />
          </Button>
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate text-base font-semibold">
            {t.subject ?? "(konu yok)"}
          </div>
          <div className="truncate text-xs text-muted-foreground">
            {t.candidate?.full_name} &lt;{t.candidate?.email}&gt;
            {t.job && ` · ${t.job.title}`}
          </div>
        </div>
      </header>
      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        {t.messages.map((m) => (
          <Card key={m.id} className={m.direction === "outbound" ? "ml-12 bg-muted/30" : "mr-12"}>
            <CardContent className="space-y-2 p-3 text-sm">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {m.direction === "outbound" ? "Giden" : "Gelen"} · {m.from_email}
                </span>
                <span>{formatDate(m.received_at)}</span>
              </div>
              {m.body_text ? (
                <p className="whitespace-pre-wrap">{m.body_text}</p>
              ) : m.body_html ? (
                <div
                  className="prose prose-sm max-w-none"
                  // backend ensures this came through our own send_email path or webhook
                  dangerouslySetInnerHTML={{ __html: m.body_html }}
                />
              ) : (
                <p className="text-muted-foreground">(boş mesaj)</p>
              )}
            </CardContent>
          </Card>
        ))}
        {t.messages.length === 0 && (
          <p className="text-sm text-muted-foreground">Henüz mesaj yok.</p>
        )}
      </div>
      <div className="space-y-2 border-t p-4">
        <Textarea
          rows={3}
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder="Yanıtınızı yazın…"
        />
        <div className="flex justify-end">
          <Button onClick={() => send.mutate()} disabled={!reply.trim() || send.isPending}>
            Gönder
          </Button>
        </div>
      </div>
    </div>
  )
}
